import { FilterListItemValueType, FilterListItem } from '@common/filter/models/filter-list-item.model';
import { prop } from '@common/lang/lang';
import * as moment from 'moment';
import { RuleSet, Rule } from 'ngx-angular-query-builder';




export interface NamedItem<T, TU> {
  name: string;
  keyCbk: (T) => any;
  mapCbk: (T) => TU;
  sortCbk: FilterListItemSortCallback;
}

export type FilterListItemSortCallback = (a: FilterListItem, b: FilterListItem) => number;

export const valueSort: FilterListItemSortCallback = (a, b) => a.value > b.value ? 1 : -1;
export const labelSort: FilterListItemSortCallback = (a, b) => a.label > b.label ? 1 : -1;

export const asDate = (d: Date): string => moment(d).format('DD/MM/YYYY');

export const mapDate = <TK extends keyof any>(prop: TK) => {
  return getMapCbk((x: any) => x[prop], (x: any) => asDate(x[prop] as Date));
}


export function getMapCbk<T>(valueCallback, nameCallback = undefined): (t: T) => FilterListItem {
  return (x: T) => {
    return {
      label: nameCallback != undefined ? nameCallback(x) : valueCallback(x),
      name: nameCallback != undefined ? nameCallback(x) : valueCallback(x),
      value: valueCallback(x)
    } as FilterListItem;
  }
}

/**
 * This class can be used to prepare multiple collections (of mapped objects based on distinct values )
 * from a single array. It is used to create distinct sets of values based on properties of objects in the
 * array
 *
 * The array elements are iterated once
 * for each item, each mapping configurations is processed and any values collected
 * after the array has been iterated,
 * mapping configurations resulting collection is sorted
  */
export class ArrayToFilterListItemBuilder<T>
{
  private distinctMaps: Array<NamedItem<T, FilterListItem>> = [];

  constructor(private readonly array: T[]) {
  }

  /**
   * Adds a named mapping callback distinct on a given key callback
   * A collection of filter list items will be built from the overall array under
   * the name, with values distinct by the key callback and consisting of values mapped
   * by the map callback
   * @param name the name of the set, used to retrieve the options after building
   * @param keyCbk the callback function used to determine a distinct value 
   * @param mapCbk the callback used to map the item to a value 
   * @param sortCbk optional sort callback used to sort the items after building. If not specified the items are sorted by the value 
   */
  add(name: string, keyCbk: (t: T) => any, mapCbk: (t: T) => FilterListItem, sortCbk?: FilterListItemSortCallback) {
    this.distinctMaps.push({
      name: name,
      keyCbk: keyCbk,
      mapCbk: mapCbk,
      sortCbk: sortCbk ? sortCbk : valueSort
    });
  }

  /**
   * A convenience method to add a mapping callback for a given name based simple on single property 
   * @param name the name of the set, used to retrieve the options after building
   * @param propKey the property that is used to determine distinct values for the set
   * @param mapCbk the callback used to map the item to a value
   * @param sortCbk optional sort callback used to sort the items after building. If not specified the items are sorted by the value
   */
  forProperty<TK extends keyof T>(name: string, propKey: TK, valueCbk: (t: T) => FilterListItemValueType, sortCbk?: FilterListItemSortCallback) {
    const keyCbk = (t: T) => prop(t, propKey);
    this.add(name, keyCbk, getMapCbk(keyCbk, valueCbk), sortCbk );
  }

  /** Iterate once over array and produce result based on distinctMaps */
  build(): Map<string, FilterListItem[]> {
    const namedSets = new Map<string, Set<string>>();
    const resultSets = new Map<string, FilterListItem[]>();

    // set for each thing we want to make distinct map for
    this.distinctMaps.forEach(m => {
      namedSets.set(m.name, new Set<string>());
      resultSets.set(m.name, []);
    });

    // iterate over array
    for (var item of this.array) {

      // iterate over each item to collect into a set of distinct values 
      this.distinctMaps.forEach(m => {
        const key = m.keyCbk(item);
        const currentSet = namedSets.get(m.name);
        if (!currentSet.has(key)) {
          currentSet.add(key);
          resultSets.get(m.name).push(m.mapCbk(item));
        }
      });

    }

    // iterate over collection to apply sort
    this.distinctMaps.forEach(m => {
      resultSets.get(m.name).sort(m.sortCbk);
    });

    return resultSets;
  }
}
